import { XCircle, X } from "lucide-react"
import { useTheme } from "../../contexts/ThemeContext"
import { navigateTo } from "../../utils/navigation"
import { useEffect, useState } from "react"
import useTranslation from "../../hooks/useTranslation"
import { useDispatch, useSelector } from "react-redux"
import { 
  verifyPayment, 
  createOrderIntent, 
  selectPaymentVerifying, 
  selectPaymentVerified, 
  selectPaymentError,
  selectPaymentIntent,
  clearPaymentState
} from "../../features/payment/paymentSlice"
import { selectToken } from "../../features/auth/authSlice"
import { selectLanguage } from "../../features/language/languageSlice"
import type { AppDispatch, RootState } from "../../store"
import brandManager from "../../services/brandManager"

export default function ErrorScreen() {
  const { primaryColor } = useTheme()
  const { t } = useTranslation()
  const dispatch = useDispatch<AppDispatch>()
  
  // Redux selectors
  const token = useSelector(selectToken)
  const language = useSelector(selectLanguage)
  const verifying = useSelector(selectPaymentVerifying)
  const verified = useSelector(selectPaymentVerified)
  const verificationError = useSelector(selectPaymentError)
  const paymentIntent = useSelector(selectPaymentIntent)
  
  const [lastPaymentData, setLastPaymentData] = useState<{
    orderId: number
    clientSecret: string
    paymobUrl: string
  } | null>(null)

  // Store the original site URL with table and branch info for future orders
  // This ensures that if the user wants to make a new order, they can return
  // to the same table and branch without scanning QR code again
  const [originalSiteUrl, setOriginalSiteUrl] = useState<string>('')
  
  // Track error type for better user messaging
  const [errorType, setErrorType] = useState<'payment_failed' | 'navigation_back' | 'general'>('general')
  
  // Track if we're processing a retry or verification
  const [isProcessing, setIsProcessing] = useState(false)

  useEffect(() => {
    // Prevent back navigation immediately when error screen loads
    const preventBack = () => {
      window.history.pushState(null, '', window.location.href)
    }
    
    // Add current page to history to prevent going back
    window.history.pushState(null, '', window.location.href)
    window.addEventListener('popstate', preventBack)
    
    // Get the original site URL from sessionStorage or create it from current URL
    const storedOriginalUrl = sessionStorage.getItem("originalSiteUrl")
    if (storedOriginalUrl) {
      setOriginalSiteUrl(storedOriginalUrl)
    } else {
      // If no stored URL, try to get it from the current URL
      const currentUrl = window.location.href
      // Extract the base URL with query parameters (table, branch, etc.)
      const urlObj = new URL(currentUrl)
      const baseUrl = urlObj.origin + urlObj.pathname
      const queryParams = urlObj.searchParams
      
      // Keep important parameters like table, branch, qr code, etc.
      // These are essential for identifying the table and branch
      const importantParams = new URLSearchParams()
      Array.from(queryParams.entries()).forEach(([key, value]) => {
        if (['table', 'branch', 'qr'].includes(key)) {
          importantParams.set(key, value)
        }
      })
      
      const newOriginalUrl = importantParams.toString() ? `${baseUrl}?${importantParams.toString()}` : baseUrl
      setOriginalSiteUrl(newOriginalUrl)
      // Store this URL for future use, even if user makes new orders
      sessionStorage.setItem("originalSiteUrl", newOriginalUrl)
    }

    // Check for payment intent in sessionStorage (new flow)
    const payIntent = sessionStorage.getItem('pay_intent')
    if (payIntent) {
      try {
        const intent = JSON.parse(payIntent)
        
        // Check if payment intent has expired (more than 30 minutes old)
        const intentAge = Date.now() - new Date(intent.timestamp).getTime()
        const maxAge = 30 * 60 * 1000 // 30 minutes
        
        if (intentAge > maxAge) {
          sessionStorage.removeItem('pay_intent')
          sessionStorage.removeItem('pay_state')
        }
      } catch (e) {
        sessionStorage.removeItem('pay_intent')
      }
    }

    const paymentData = sessionStorage.getItem("paymentData")
    if (paymentData) {
      try {
        const parsed = JSON.parse(paymentData)
        setLastPaymentData(parsed)
        
        // Determine error type based on payment data
        if (parsed.isBackNavigation) {
          setErrorType('navigation_back')
        } else if (parsed.reason && parsed.reason.includes('Payment failed')) {
          setErrorType('payment_failed')
        } else {
          setErrorType('general')
        }
        
      } catch (e) {
      }
    }
    
    // Also check URL parameters for error type
    const urlParams = new URLSearchParams(window.location.search)
    const reason = urlParams.get('reason')
    if (reason === 'navigation_back') {
      setErrorType('navigation_back')
    } else if (reason === 'payment_abandoned') {
      setErrorType('payment_failed')
    }
    
    // Cleanup function
    return () => {
      window.removeEventListener('popstate', preventBack)
    }
  }, [])

  // Cleanup effect
  useEffect(() => {
    return () => {
      // Clear payment state when component unmounts
      dispatch(clearPaymentState())
    }
  }, [dispatch])

  // Handle payment verification results
  useEffect(() => {
    if (!verifying && verified) {
      setIsProcessing(false)
      
      if (verified === 'paid') {
        
        // Get order ID from payment intent or session storage
        let orderId = ''
        if (paymentIntent) {
          orderId = paymentIntent.orderId
        } else {
          const payIntent = sessionStorage.getItem('pay_intent')
          if (payIntent) {
            try {
              const intent = JSON.parse(payIntent)
              orderId = intent.orderId
            } catch (e) {
            }
          }
        }
        
        if (orderId) {
          navigateTo('success')
          // Set orderId in sessionStorage for SuccessScreen to read
          sessionStorage.setItem('successOrderId', orderId)
        } else {
          navigateTo('checkout')
        }
      } else {
        // Clear payment state and stay on error page
        dispatch(clearPaymentState())
      }
    }
    
    if (verificationError) {
      setIsProcessing(false)
      // Clear payment state and stay on error page
      dispatch(clearPaymentState())
    }
  }, [verifying, verified, verificationError, paymentIntent, dispatch])

  const handleClose = () => {
    
    try {
      // Clear all data
      sessionStorage.clear()
      localStorage.clear()
      
      // Clear browser history completely
      if (window.history && window.history.replaceState) {
        window.history.replaceState(null, '', '/')
      }
      
      // Prevent back button navigation
      const preventBack = () => {
        window.history.pushState(null, '', '/')
      }
      
      // Add event listener to prevent back navigation
      window.addEventListener('popstate', preventBack)
      
      // Use replace instead of href to prevent going back
      window.location.replace('/')
      
      // Remove the event listener after a short delay
      setTimeout(() => {
        window.removeEventListener('popstate', preventBack)
      }, 1000)
      
    } catch (error) {
      // Force redirect
      window.location.replace('/')
    }
  }

  const handleRetry = async () => {
    
    // Prevent back navigation during retry
    const preventBack = () => {
      window.history.pushState(null, '', window.location.href)
    }
    window.addEventListener('popstate', preventBack)
    
    setIsProcessing(true)
    
    try {
      // First, try to use the stored Paymob URL if it exists and is still valid
      const storedPaymobUrl = sessionStorage.getItem('paymob_url') || sessionStorage.getItem('last_paymob_url')
      const payIntent = sessionStorage.getItem('pay_intent')
      
      
      
      // Try to use stored Paymob URL first (even without pay_intent)
      if (storedPaymobUrl) {
        
        // Set payment state back to pending
        sessionStorage.setItem('pay_state', 'pending')
        
        // Show success message before redirect
        
        // Don't use replaceState with cross-origin URLs - just redirect directly
        // window.history.replaceState(null, '', storedPaymobUrl) // This causes SecurityError
        
        // Redirect to the same Paymob URL directly
        window.location.href = storedPaymobUrl
        return
      }
      
      // If no stored URL but we have pay_intent, try to rebuild it
      if (payIntent && !storedPaymobUrl) {
        try {
          const intentData = JSON.parse(payIntent)
          const intentAge = Date.now() - new Date(intentData.timestamp).getTime()
          const maxAge = 30 * 60 * 1000 // 30 minutes
          
          if (intentAge <= maxAge) {
            
            // Extract the actual client_secret from the enhanced payment token
            let clientSecret = intentData.paymentToken
            try {
              const enhancedToken = JSON.parse(intentData.paymentToken)
              clientSecret = enhancedToken.token
            } catch (e) {
              // If parsing fails, use the token as is (fallback)
            }
            
            // Build Paymob URL
            const paymobPublicKey = process.env.REACT_APP_PAYMOB_PUBLIC_KEY
            if (paymobPublicKey) {
              const returnUrl = `${window.location.origin}/payment/return`
              const paymobUrl = `https://ksa.paymob.com/unifiedcheckout/?publicKey=${encodeURIComponent(paymobPublicKey)}&clientSecret=${clientSecret}&return_url=${encodeURIComponent(returnUrl)}`
              
              // Store the rebuilt URL for future use
              sessionStorage.setItem('paymob_url', paymobUrl)
              sessionStorage.setItem('pay_state', 'pending')
              
              // Don't use replaceState with cross-origin URLs
              // window.history.replaceState(null, '', paymobUrl) // This causes SecurityError
              
              // Redirect to Paymob directly
              window.location.href = paymobUrl
              return
            }
          } else {
            sessionStorage.removeItem('pay_intent')
            sessionStorage.removeItem('pay_state')
            sessionStorage.removeItem('paymob_url')
          }
        } catch (e) {
          sessionStorage.removeItem('pay_intent')
          sessionStorage.removeItem('pay_state')
          sessionStorage.removeItem('paymob_url')
        }
      }
      
      // If no valid stored URL or intent, try to create a simple Paymob URL
      
      const paymobPublicKey = process.env.REACT_APP_PAYMOB_PUBLIC_KEY
      if (paymobPublicKey) {
        // Try to get client secret from any available source
        const paymentData = sessionStorage.getItem("paymentData")
        let clientSecret = null
        
        if (paymentData) {
          try {
            const parsed = JSON.parse(paymentData)
            clientSecret = parsed.clientSecret
          } catch (e) {
          }
        }
        
        if (clientSecret) {
          const returnUrl = `${window.location.origin}/payment/return`
          const paymobUrl = `https://ksa.paymob.com/unifiedcheckout/?publicKey=${encodeURIComponent(paymobPublicKey)}&clientSecret=${clientSecret}&return_url=${encodeURIComponent(returnUrl)}`
          
          sessionStorage.setItem('paymob_url', paymobUrl)
          sessionStorage.setItem('pay_state', 'pending')
          
          // Don't use replaceState with cross-origin URLs
          // window.history.replaceState(null, '', paymobUrl) // This causes SecurityError
          
          // Redirect to Paymob directly
          window.location.href = paymobUrl
          return
        }
      }
      
      // If no valid stored URL, we need to regenerate payment intent
      // Try to get token from multiple sources
      let authToken = token
      if (!authToken) {
        authToken = localStorage.getItem('auth_token') || 
                   sessionStorage.getItem('auth_token') || 
                   sessionStorage.getItem('orderToken') || 
                   sessionStorage.getItem('currentOrderToken') ||
                   sessionStorage.getItem('authToken')
      }
      
      if (!authToken) {
        // No authentication token available for retry
        // Instead of redirecting to checkout, show error message
        alert('لا يمكن إعادة المحاولة - يرجى العودة للصفحة الرئيسية وبدء طلب جديد')
        return
      }
      
      // Check if token is valid by making a simple API call
      try {
        const response = await fetch(`${brandManager.apiUrl}/settings`, {
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          }
        })
        
        if (!response.ok) {
          throw new Error(`Token validation failed: ${response.status} ${response.statusText}`)
        }
      } catch (tokenError) {
        alert('انتهت صلاحية الجلسة - يرجى العودة للصفحة الرئيسية وبدء طلب جديد')
        return
      }
      
      // Get stored order data to recreate the payment
      const orderData = sessionStorage.getItem('orderData')
      let orderRequest = null
      
      if (orderData) {
        const parsedOrderData = JSON.parse(orderData)
        
        // Check if we have the correct order data structure
        if (parsedOrderData.cartId || parsedOrderData.orderId) {
          // Reconstruct order data for createOrderIntent
          orderRequest = {
            cartId: parsedOrderData.cartId || parsedOrderData.orderId,
            platform: "qr",
            typeId: parsedOrderData.urlParams?.typeId || 2,
            notes: parsedOrderData.notes || "",
            paymentMethod: "online",
            branchId: parsedOrderData.urlParams?.branchId || 1,
            phone: parsedOrderData.customerInfo?.phone || parsedOrderData.phone,
            ...(parsedOrderData.urlParams?.tableId && { 
              tableId: parseInt(parsedOrderData.urlParams.tableId) 
            }),
            ...(parsedOrderData.urlParams?.tablePrice && { 
              tablePrice: parseFloat(parsedOrderData.urlParams.tablePrice) 
            })
          }
        }
      }
      
      // If we don't have valid order data, try to get it from URL parameters
      if (!orderRequest) {
        const urlParams = new URLSearchParams(window.location.search)
        const branchId = urlParams.get('branchId') ? parseInt(urlParams.get('branchId')!) : 1
        const typeId = urlParams.get('typeId') ? parseInt(urlParams.get('typeId')!) : 2
        const tableId = urlParams.get('tableId') ? parseInt(urlParams.get('tableId')!) : undefined
        const tablePrice = urlParams.get('tablePrice') ? parseFloat(urlParams.get('tablePrice')!) : undefined
        
        
        // Get cart data from sessionStorage
        const cartData = sessionStorage.getItem('apiCartData')
        if (!cartData) {
          // Try alternative cart data keys
          const altCartData = sessionStorage.getItem('cartData') || sessionStorage.getItem('orderCartData')
          if (!altCartData) {
            // Instead of throwing error, show user-friendly message
            alert('لا يمكن إعادة المحاولة - يرجى العودة للصفحة الرئيسية وبدء طلب جديد')
            return
          }
          const parsedAltCartData = JSON.parse(altCartData)
          orderRequest = {
            cartId: parsedAltCartData.cartId || parsedAltCartData.id || 1, // Fallback to 1
            platform: "qr",
            typeId: typeId,
            notes: "",
            paymentMethod: "online",
            branchId: branchId,
            phone: "01234567890", // Default phone number
            ...(tableId && { tableId: tableId }),
            ...(tablePrice && { tablePrice: tablePrice })
          }
        } else {
          const parsedCartData = JSON.parse(cartData)
          orderRequest = {
            cartId: parsedCartData.id,
            platform: "qr",
            typeId: typeId,
            notes: "",
            paymentMethod: "online",
            branchId: branchId,
            phone: "01234567890", // Default phone number
            ...(tableId && { tableId: tableId }),
            ...(tablePrice && { tablePrice: tablePrice })
          }
        }
      }
      
      
      // Validate order request
      if (!orderRequest.cartId) {
        alert('لا يمكن إعادة المحاولة - يرجى العودة للصفحة الرئيسية وبدء طلب جديد')
        return
      }
      
      // Clear any previous payment state before creating new intent
      sessionStorage.removeItem('pay_state')
      sessionStorage.removeItem('pay_intent')
      // Don't remove paymob_url here - we'll remove it after successful creation
      
      
      // Create new order intent
      const result = await dispatch(createOrderIntent({
        orderData: orderRequest,
        token: authToken,
        lang: language
      })).unwrap()
      
      
      // The createOrderIntent function now handles building and storing the Paymob URL
      // So we just need to get it from sessionStorage and redirect
      const newPaymobUrl = sessionStorage.getItem('paymob_url')
      if (newPaymobUrl) {
        // Set payment state to pending
        sessionStorage.setItem('pay_state', 'pending')
        
        // Don't use replaceState with cross-origin URLs
        // window.history.replaceState(null, '', newPaymobUrl) // This causes SecurityError
        
        // Redirect to Paymob directly
        window.location.href = newPaymobUrl
      } else {
        alert('فشل في إنشاء رابط الدفع - يرجى المحاولة مرة أخرى')
        return
      }
      
    } catch (error) {
      
      // Show user-friendly error message instead of redirecting to checkout
      alert(`حدث خطأ أثناء إعادة المحاولة: ${error instanceof Error ? error.message : 'خطأ غير معروف'} - يرجى العودة للصفحة الرئيسية وبدء طلب جديد`)
      
      // Clear all payment data on error
      sessionStorage.removeItem("paymentData")
      sessionStorage.removeItem("pay_intent")
      sessionStorage.removeItem("pay_state")
      sessionStorage.removeItem("orderData")
      sessionStorage.removeItem("paymob_url")
      sessionStorage.removeItem("last_paymob_url")
      
    } finally {
      setIsProcessing(false)
    }
  }

  const handleGoToSuccess = () => {
    if (lastPaymentData?.orderId) {
      const currentParams = new URLSearchParams(window.location.search)
      currentParams.set('orderId', lastPaymentData.orderId.toString())
      window.location.href = `/order-success?${currentParams.toString()}`
    } else {
      navigateTo("home")
    }
  }

  const handleGoToOriginalSite = () => {
    // Return to the original site with table and branch information
    if (originalSiteUrl) {
      window.location.href = originalSiteUrl
    } else {
      // Fallback to home if no original URL
      navigateTo("home")
    }
  }

  return (
    // خلفية وردية متدرجة مثل Figma
    <div className="min-h-screen w-full bg-gradient-to-b from-rose-100 via-rose-50 to-white relative flex items-center justify-center px-4">

      {/* زر الإغلاق خارج الكارد أعلى اليمين */}
      <button
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
          handleClose()
        }}
        onMouseDown={(e) => {
          e.preventDefault()
          e.stopPropagation()
        }}
        aria-label="close"
        className="absolute top-6 right-6 h-10 w-10 rounded-xl bg-white shadow-md ring-1 ring-black/5 grid place-items-center hover:bg-neutral-50 transition-colors cursor-pointer z-50"
        style={{ zIndex: 9999 }}
      >
        <X className="w-4.3 h-4.3 text-neutral-600 pointer-events-none" />
      </button>
      <div className="w-full max-w-sm min-h-screen flex flex-col justify-center items-center mx-auto relative">
    {/* الجزء العلوي (الأيقونة + النص) */}
  <div className="flex flex-col justify-center items-center flex-1">
    <div className="mx-auto mb-3 relative grid place-items-center">
      <img 
        src="/x.svg"   // الملف داخل public
        alt="X Icon" 
        className="h-20 w-20"
      />            
    </div>

    {/* العنوان */}
    <h1 className="text-2xl  text-black font-['IBM_Plex_Sans_Arabic']">
      {errorType === 'navigation_back' ? t("payment.navigationBack") : t("payment.failed")}
    </h1>
    
    {/* رسالة توضيحية حسب نوع الخطأ */}
    {errorType === 'navigation_back' && (
      <p className="text-center text-gray-600 mt-2 px-4 font-['IBM_Plex_Sans_Arabic']">
        {t("payment.navigationBackMessage")}
      </p>
    )}
    
    {errorType === 'payment_failed' && (
      <p className="text-center text-gray-600 mt-2 px-4 font-['IBM_Plex_Sans_Arabic']">
        {t("payment.failedMessage")}
      </p>
    )}
    
    {/* Show processing message */}
    {isProcessing && (
      <div className="mt-4 text-center">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto mb-2"></div>
        <p className="text-sm text-gray-600 font-['IBM_Plex_Sans_Arabic']">
          {verifying ? 'جاري التحقق من حالة الدفع...' : 'جاري إعادة توجيهك لصفحة الدفع...'}
        </p>
        <p className="text-xs text-gray-500 mt-1 font-['IBM_Plex_Sans_Arabic']">
          يرجى الانتظار، سيتم توجيهك لصفحة paymob لدفع نفس الطلب
        </p>
        <p className="text-xs text-blue-600 mt-1 font-['IBM_Plex_Sans_Arabic']">
          لن يتم توجيهك لصفحة checkout - سيتم إرسالك مباشرة لصفحة الدفع
        </p>
      </div>
    )}
  </div>

  {/* الزر مثبت أسفل الشاشة ويأخذ w-full */}
  <div className="fixed bottom-5 left-2 right-2 space-y-3">
    <button
      onClick={handleRetry}
      disabled={isProcessing}
      className="w-full py-4 rounded-xl text-white font-bold text-lg font-['IBM_Plex_Sans_Arabic'] shadow-[0_10px_18px_rgba(0,0,0,0.08)] hover:opacity-95 active:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
      style={{ backgroundColor: primaryColor || "#E1582F" }}
    >
      {isProcessing ? 'جاري المعالجة...' : t("errors.tryAgain")}
    </button>
    

  </div>
</div>


    </div>
  )
}
